import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./styles/ReportPage.css";
import DownloadReportWindow from "../../components/DownloadReportWindow";
import search from "/assets/icon/Search.svg";
import UserStatus from "../../components/UserStatus";

const formatDate = (date) => {
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  return new Date(date).toLocaleDateString("en-GB", options);
};

function ShowDetailedReport() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showDownloadReport, setShowDownloadReport] = useState(false);
  const [weatherData, setWeatherData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sensorData, setSensorData] = useState([]);

  const [userId] = useState(1); // Assuming a fixed userId for the example

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleDownloadReportClick = () => {
    setShowDownloadReport(true);
  };

  const handleDownloadReportClose = () => {
    setShowDownloadReport(false);
  };

  const fetchWeatherData = useCallback(async () => {
    if (!searchQuery) {
      setWeatherData([]);
      return;
    }
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/polesSearch?searchQuery=${searchQuery}`
      );
      setWeatherData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchWeatherData();
  }, [fetchWeatherData]);

  const fetchPoleData = useCallback(async (poleId) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/getPoleById/${poleId}`
      );
      setSelectedItem(response.data);
      setSensorData(response.data.sensorData || []); // Assuming sensorData is part of the response
    } catch (error) {
      console.error("Error fetching pole data:", error);
    }
  }, []);

  const handleItemClick = (item) => {
    setSearchQuery(item.name); // Set searchQuery to the selected item's name
    setSelectedItem(item);
    fetchPoleData(item.id);
    setWeatherData([item]); // Display only the selected item
  };

  const handleClearSelection = () => {
    setSearchQuery("");
    setSelectedItem(null);
    setSensorData([]);
    setWeatherData([]);
  };

  // สร้างชื่อไฟล์
  const createFileName = () => {
    if (!selectedItem) return "report";
    return `${selectedItem.name}_detailed_report`;
  };

  return (
    <div className="containerPage">
      <div className="showWeatherReport">
        <h4 className="downloadReport" onClick={handleDownloadReportClick}>
          ดาวน์โหลดรายงาน
        </h4>
        <h1>รายงานรายละเอียดของแต่ละเสา (ติดตั้งกล้อง จอ การปรับไฟ)</h1>
        <p>ค้นหารหัสเสา</p>
        <div className="searchbarreport">
          <div className="search-bar">
            <img src={search} alt="search" className="search-user" />
            <input
              type="text"
              placeholder="ค้นหารหัสเสา ชื่อเสา ตำแหน่งติดตั้ง"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {selectedItem && (
              <button className="clear-button" onClick={handleClearSelection}>
                &times;
              </button>
            )}
          </div>
        </div>

        <div className="searchResults">
          {searchQuery && (
            <>
              {weatherData.map((data) => (
                <div
                  key={data.id}
                  className={`resultItem ${
                    selectedItem && selectedItem.id === data.id
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleItemClick(data)}
                  style={{ cursor: "pointer" }}
                >
                  <p>
                    <strong>ชื่อเสา</strong> {data.name}
                  </p>
                  <p>
                    <strong>Location</strong> {data.location}
                  </p>
                  <p>
                    <strong>ประเภทเสา</strong> {data.pole_type}
                  </p>
                  <p>
                    <strong>Latitude</strong> {data.lat} , {data.lng}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>

        <hr />
        {selectedItem && (
          <div className="selectedItemDetailsPoles">
            <table>
              <thead>
                <tr>
                  <th>อุปกรณ์ที่ติดตั้ง</th>
                  <th>ชื่ออุปกรณ์</th>
                  <th>รุ่นอุปกรณ์</th>
                  <th>วันที่ติดตั้ง</th>
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {selectedItem.cameras && selectedItem.cameras.length > 0 && (
                  <>
                    {selectedItem.cameras.map((camera, index) => (
                      <tr
                        key={`camera-${camera.id}-${index}`}
                        className="cameraDetails"
                      >
                        <td>{index === 0 ? "CCTV" : ""}</td>
                        <td>{camera.name}</td>
                        <td>{camera.model}</td>
                        <td>{formatDate(camera.installation_date)}</td>
                        <td className="withUserstatus">
                          <UserStatus isOnline={camera.is_broadcast} />
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                {selectedItem.has_screen && (
                  <tr key="led-screen">
                    <td>จอ LED</td>
                    <td>{selectedItem.screen_id}</td>
                    <td>{selectedItem.screen_model}</td>
                    <td>{formatDate(selectedItem.screen_installation_date)}</td>
                    <td className="withUserstatus">
                      <UserStatus isOnline={selectedItem.has_screen} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <h3>การปรับไฟฟ้าอัตโนมัติ</h3>
            {selectedItem.schedules && selectedItem.schedules.length > 0 ? (
              <table className="scheduleTable">
                <thead>
                  <tr>
                    <th>ช่วงเวลา</th>
                    {selectedItem.schedules.map((schedule, index) => (
                      <th
                        key={`header-${schedule.schedule_id}-${index}`}
                        style={{ textAlign: "left" }}
                      >
                        {schedule.period}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ความสว่าง</td>
                    {selectedItem.schedules.map((schedule, index) => (
                      <td
                        key={`body-${schedule.schedule_id}-${index}`}
                        style={{ textAlign: "left" }}
                      >
                        {schedule.brightness}%
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="maimee">
                <p>ไม่มีข้อมูลตารางเวลาสำหรับเสานี้</p>
              </div>
            )}
          </div>
        )}

        {showDownloadReport && (
          <DownloadReportWindow
            onClose={handleDownloadReportClose}
            jsonData={{
              poleInfo: {
                name: selectedItem?.name,
                location: selectedItem?.location,
                poleType: selectedItem?.pole_type,
                latitude: selectedItem?.lat,
                longitude: selectedItem?.lng,
                id: selectedItem?.id,
              },
              sensorData: [
                ...(selectedItem?.cameras?.map((camera) => ({
                  deviceType: "CCTV",
                  name: camera.name,
                  model: camera.model,
                  installationDate: formatDate(camera.installation_date),
                  status: camera.is_broadcast ? "Online" : "Offline",
                })) || []),
                ...(selectedItem?.has_screen
                  ? [
                      {
                        deviceType: "จอ LED",
                        name: selectedItem?.screen_id,
                        model: selectedItem?.screen_model,
                        installationDate: formatDate(
                          selectedItem?.screen_installation_date
                        ),
                        status: selectedItem?.has_screen ? "Online" : "Offline",
                      },
                    ]
                  : []),
              ],
              schedules:
                selectedItem?.schedules?.map((schedule, index) => ({
                  id: `${schedule.schedule_id}-${index}`,
                  period: schedule.period,
                  brightness: schedule.brightness,
                })) || [],
            }}
            fileName={createFileName()}
            routeName="showdetailedreport"
          />
        )}
      </div>
    </div>
  );
}

export default ShowDetailedReport;
